import {
    LabeledComponentDefinition,
    ValueComponentDefinition,
    ActionComponentDefinition,
    ActionComponent,
    DisplayedComponent,
    isInputComponent,
    RestrictableComponent
} from "components/components/Component";
import { LabelElement } from "components/components/label/labelElement";
import {
    AttributeRestriction,
    RestrictionAttribute
} from "components/restrictions/Restrictions";
import { InputHTMLElement, SelectHTMLElement } from "components/input/baseHTMLElementInput";
import { Tooltip } from "components/components/tooltip/tooltip";
import { fireDataChangedEvent, fireInitDataChangedEvent } from "components/form/dataChangedEventDetail";
import { FocusManager } from "components/pages/focusManager";
import { ValueBaseComponent } from "components/components/ValueBaseComponent";
import $ from "jquery";

export abstract class InputBaseComponent<U extends InputBaseDefinition> extends ValueBaseComponent<U> implements RestrictableComponent, DisplayedComponent, ActionComponent {

    constructor(schema: U) {
        super(schema);
        this.setDefinition(schema);
    }

    public setDefinition(schema: U) {
        if (schema.labelKey == null || schema.labelKey.length === 0) {
            this.definition.labelWidth = 0;
        }
        this.definition.emptyWidth = this.definition.emptyWidth ?? 0;
        if (this.definition.inputWidth == null) {
            this.definition.inputWidth = 16 - (this.definition.labelWidth || 0) - this.definition.emptyWidth;
        }
        this.definition.width = this.definition.width ?? 16;
        this.definition.labelWidth = this.definition.labelWidth ?? 16;
    }

    public renderTemplate(template: any, params?: any): Element {
        let jqueryView = $(template(Object.assign(
            this.definition,
            params ?? {},
        )));
        this.view = jqueryView[0];
        return this.view;
    }

    public tooltipBindElement(selectors: string) {
        Tooltip.bindElement(this.view.querySelector(selectors));
    }

    public genericBindEventsInputHTMLElement(element: InputHTMLElement | SelectHTMLElement): void {
        if (element != null && this.view != null) {
            element.addEventListener("change", () => {
                const data = element.data;
                const isEqual = element.isEqualToInitialValue(data);
                if (!isEqual) {
                    element.initialValue.reassignvalue(data);
                    fireDataChangedEvent(this, this.view, data);
                }
            });
        }
    }

    public abstract setValidationStatus(valid: boolean, message?: string): void;

    public applyRestrictions(restrictions: AttributeRestriction) {
        if (restrictions == null || restrictions.attributes == null) {
            return;
        }
        super.applyRestrictions(restrictions);
        if (isInputComponent(this)) {
            this.setReadOnly(restrictions.attributes.includes(RestrictionAttribute.readonly));
            this.setRequired(restrictions.attributes.includes(RestrictionAttribute.required));
        }
    }
}


export abstract class SimpleInputBaseComponent<U extends SimpleInputBaseDefinition> extends InputBaseComponent<U> {

    private static readonly TIMEOUT_FINISHEDTYPING: number = 750; //ms 0,75 seg
    private typingTimer: number;

    constructor(schema: U) {
        super(schema);
    }

    public genericBindEvents(element: InputHTMLElement | SelectHTMLElement, enableKeyUpFinishedTyping?: boolean): void {
        this.genericBindEventsHTMLElement(element);
        this.genericBindEventsInputHTMLElement(element);

        if (enableKeyUpFinishedTyping != null && enableKeyUpFinishedTyping === true) {
            this.eventKeyUpFinishedTyping(element);
        }
    }

    public genericBindEventsHTMLElement(element: HTMLElement): void {
        if (element != null) {
            if (this.definition.tabOnEnter === true) {
                element.addEventListener("keydown", (e) => {
                    if (e.key === "Enter") {
                        e.stopPropagation();
                        e.preventDefault();
                        FocusManager.focusNext();
                    }
                });
            }
        }
    }

    public eventKeyUpFinishedTyping(element: InputHTMLElement | SelectHTMLElement) {
        element.addEventListener("keyup", () => {
            const data = element.data;
            const isEqual = element.isEqualToInitialValue(data);
            if (!isEqual) {
                let doneTyping = () => element.dispatchEvent(new Event("change"));
                clearTimeout(this.typingTimer);
                this.typingTimer = window.setTimeout(doneTyping, SimpleInputBaseComponent.TIMEOUT_FINISHEDTYPING);
            }
        });
    }

    public eventFireDataChanged(isNotInitDataEvent: boolean, data: any) {
        if (isNotInitDataEvent) {
            fireDataChangedEvent(this, this.view, data);
        } else {
            fireInitDataChangedEvent(this, this.view, data);
        }
    }
}

export abstract class DateInputBaseComponent<U extends SimpleInputBaseDefinition> extends SimpleInputBaseComponent<U> {

    constructor(schema: U) {
        super(schema);
    }

    public renderTemplate(template: any, params?: any): Element {
        let d = document.createElement("div");
        d.innerHTML = template(Object.assign(
            this.definition, params ?? {}
        ));
        this.view = d.firstChild as HTMLElement;
        return this.view;
    }

    /**
     * You have to overwrite the default one, because with the DataRangePicker component, 
     * you have to make a set again. Line: "element.data = data;". Otherwise, the component
     * remains empty string.
     * 
     * @param element 
     */
    public genericBindEventsInputHTMLElement(element: InputHTMLElement): void {
        if (element != null && this.view != null) {
            element.addEventListener("change", () => {
                const data = element.data;
                const isEqual = element.isEqualToInitialValue(data);
                if (!isEqual) {
                    element.initialValue.reassignvalue(data);
                    //This is the different line
                    element.data = data;
                    fireDataChangedEvent(this, this.view, data);
                }
            });
        }
    }

    public setRequired(required: boolean) {
        const inputLabel = this.view.querySelector("s6-input-label");
        if (inputLabel != null) {
            (this.view.querySelector("s6-input-label") as LabelElement).required = required;
        }
    }
}

export interface InputBaseDefinition extends LabeledComponentDefinition, ValueComponentDefinition, ActionComponentDefinition {
    inputWidth?: number;
    emptyWidth?: number;
}

export interface SimpleInputBaseDefinition extends InputBaseDefinition {
    tabOnEnter?: boolean;
    disableFocusKeyboard?: boolean;
}

export interface DateInputBaseDefinition extends SimpleInputBaseDefinition {
    invalidDateDaysOfWeeks?: number[];
}
